import type {
  DeactivateResult,
  MergeConflicts,
  MergeResult,
  OrgActivityPage,
  OrgGraph,
  OrgSourceMapping,
  PaginatedClientsList,
  RenameResult,
  UnlinkResult,
} from '@@/types/Organizations';
import type {
  DeactivateOrganizationParams,
  DeactivateResultData,
  GetActivitiesParams,
  GetClientsParams,
  GetOrgGraphParams,
  MergeOrganizationParams,
  MergeResultData,
  OrganizationsRepository,
  OrgActivitiesResponseData,
  OrgClientsResponseData,
  OrgGraphData,
  OrgSourceMappingsResponseData,
  RenameOrganizationParams,
  RenameResultData,
  UnlinkResultData,
  UnlinkSourceMappingParams,
} from '@@/types/OrganizationsAPI';
// Imported explicitly rather than auto-imported so tests can vi.mock the module.
import { useOrganizationServiceApi } from '~/composables/useOrganizationServiceApi';
import { apiErrorStatus, errorBody } from '~/utils/organizations/apiErrors';
import {
  serializeDeactivateResult,
  serializeMergeConflict,
  serializeMergeResult,
  serializeOrgActivityPage,
  serializeOrgClientsList,
  serializeOrgGraph,
  serializeOrgSourceMappings,
  serializeRenameResult,
  serializeUnlinkResult,
} from '../serializers/organizations';

/*
 * Drops unset params rather than sending `?state=` or `?cursor=null`. The API already reads a
 * blank value as the default, so this only keeps request URLs honest about what was asked.
 */
const compactQuery = (params: Record<string, string | number | null | undefined>) =>
  Object.fromEntries(
    Object.entries(params).filter(([, value]) => value !== null && value !== undefined && String(value).trim() !== ''),
  );

const orgPath = (id: string, action: string) => `/organizations/${encodeURIComponent(id)}/${action}`;

/*
 * A blank audit note is sent as no note at all. The service keeps whatever string it gets, so an
 * all-whitespace reason would otherwise land in the audit log as a meaningless entry.
 */
const withReason = (body: Record<string, string>, reason?: string) => {
  const trimmed = reason?.trim();
  return trimmed ? { ...body, reason: trimmed } : body;
};

// A merge refused because both sides hold the same slots: the 409's body lists them. Null for anything else.
const mergeConflictsIn = (error: unknown): MergeConflicts | null => {
  const { conflicts } = errorBody(error);
  return apiErrorStatus(error) === 409 && conflicts?.length
    ? { merged: false, conflicts: conflicts.map(serializeMergeConflict) }
    : null;
};

const post = <T>(id: string, action: string, body: Record<string, string>) =>
  useOrganizationServiceApi<T>(orgPath(id, action), { method: 'POST', body });

export default function Organizations(): OrganizationsRepository {
  return {
    async getClients({
      page,
      limit,
      search,
      country,
      state,
      authStatus,
      migrationStatus,
    }: GetClientsParams = {}): Promise<PaginatedClientsList> {
      const response = await useOrganizationServiceApi<OrgClientsResponseData>('/clients', {
        query: compactQuery({
          page,
          limit,
          search: search?.trim(),
          country,
          state,
          auth_status: authStatus,
          migration_status: migrationStatus,
        }),
      });
      return serializeOrgClientsList(response);
    },

    async getOrgGraph({ id, status, cursor, limit }: GetOrgGraphParams): Promise<OrgGraph> {
      const response = await useOrganizationServiceApi<OrgGraphData>(orgPath(id, 'descendants'), {
        query: compactQuery({ status, cursor, limit }),
      });
      return serializeOrgGraph(response);
    },

    async getSourceMappings(id: string): Promise<OrgSourceMapping[]> {
      const response = await useOrganizationServiceApi<OrgSourceMappingsResponseData>(orgPath(id, 'source_mappings'));
      return serializeOrgSourceMappings(response);
    },

    async renameOrganization({ id, name, reason }: RenameOrganizationParams): Promise<RenameResult> {
      const response = await post<RenameResultData>(id, 'rename', withReason({ name }, reason));
      return serializeRenameResult(response);
    },

    async deactivateOrganization({ id, reason }: DeactivateOrganizationParams): Promise<DeactivateResult> {
      const response = await post<DeactivateResultData>(id, 'deactivate', withReason({}, reason));
      return serializeDeactivateResult(response);
    },

    async mergeOrganization({
      loserId,
      survivorId,
      reason,
    }: MergeOrganizationParams): Promise<MergeResult | MergeConflicts> {
      try {
        const response = await post<MergeResultData>(loserId, 'merge', withReason({ survivor_id: survivorId }, reason));
        return serializeMergeResult(response);
      } catch (error) {
        const conflicts = mergeConflictsIn(error);
        if (conflicts) return conflicts;
        throw error;
      }
    },

    // The reason is always sent, even blank: the service requires it here and is the authority on that.
    async unlinkSourceMapping({
      id,
      sourceSystem,
      sourceEntityType,
      reason,
    }: UnlinkSourceMappingParams): Promise<UnlinkResult> {
      const response = await post<UnlinkResultData>(id, 'unlink', {
        source_system: sourceSystem,
        source_entity_type: sourceEntityType,
        reason: reason.trim(),
      });
      return serializeUnlinkResult(response);
    },

    // No limit: the service's default page (20) is what the drawer shows.
    async getActivities({ id, cursor }: GetActivitiesParams): Promise<OrgActivityPage> {
      const response = await useOrganizationServiceApi<OrgActivitiesResponseData>(orgPath(id, 'activities'), {
        query: compactQuery({ cursor }),
      });
      return serializeOrgActivityPage(response);
    },
  };
}
